import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useVideoSidebar } from "../../contexts/VideoSidebarContext";
import { useScrollLock } from "../../hooks/useScrollLock";
import { toast } from "sonner";
import {
  FaList,
  FaLock,
  FaCheckCircle,
  FaPlay,
  FaCertificate,
  FaDownload,
  FaHourglassHalf,
} from "react-icons/fa";

import {
  selectLessons,
  selectCurrentLessonId,
  selectCurrentCourseId,
  selectCourseCompleted,
  selectCanApplyForCertificate,
} from "../../store/slices/lessonProgress/slice";
import {
  selectCertificateStatus,
  selectApplicationLoading,
} from "../../store/slices/certificates/slice";

import {
  LessonsListContainer,
  LessonsHeader,
  LessonsList,
  LessonItem,
  LessonIcon,
  LessonInfo,
  SidebarLessonTitle,
  LessonDuration,
  LessonProgress,
  ProgressBar,
  ProgressFill,
  CertificateSection,
  Row,
  SidebarContainerMobile,
  CertificateIcon,
  CertificateIconCircle,
  CertificateTitle,
  CertificateDescription,
  CertificateBtn,
} from "../../screens/User/VideoPage/VideoPage.styles";

const GlobalVideoSidebar = () => {
  const { sidebarData, isOpen, closeSidebar } = useVideoSidebar();
  const navigate = useNavigate();
  const location = useLocation();

  useScrollLock(isOpen);

  // Podaci se čitaju direktno iz store-a da sidebar uvek
  // prikazuje aktuelno stanje (a ne zamrznuti snapshot)
  const lessons = useSelector(selectLessons);
  const currentLessonId = useSelector(selectCurrentLessonId);
  const storeCourseId = useSelector(selectCurrentCourseId);
  const courseCompleted = useSelector(selectCourseCompleted);
  const canApplyForCertificate = useSelector(selectCanApplyForCertificate);
  const certificateStatus = useSelector(selectCertificateStatus);
  const applicationLoading = useSelector(selectApplicationLoading);

  // Zatvori sidebar pri svakoj promeni rute
  useEffect(() => {
    closeSidebar();
  }, [location.pathname, closeSidebar]);

  if (!isOpen || !sidebarData) return null;

  const { courseId: sidebarCourseId, onLessonSelect, onCertificateAction } =
    sidebarData;
  const courseId = storeCourseId || sidebarCourseId;

  const handleLessonSelect = (lesson) => {
    if (!lesson.isUnlocked) {
      toast.error("Ova lekcija još uvek nije otključana");
      return;
    }

    closeSidebar();

    if (onLessonSelect) {
      onLessonSelect(lesson);
    } else if (courseId) {
      navigate(`/korisnicki-panel/kursevi/${courseId}/lekcije/${lesson.id}`);
    }
  };

  const handleLessonKeyDown = (event, lesson) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleLessonSelect(lesson);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div
      className="sidebar-overlay"
      onClick={closeSidebar}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(10px)",
        zIndex: 1998,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <SidebarContainerMobile onClick={(e) => e.stopPropagation()}>
        <LessonsListContainer style={{ overflow: "unset" }}>
          <LessonsHeader>
            <Row style={{ marginBottom: 0 }}>
              <FaList color="var(--accent)" />
              <h3>Lista lekcija</h3>
            </Row>
            <button onClick={closeSidebar} aria-label="Zatvori listu lekcija">
              ×
            </button>
          </LessonsHeader>

          <LessonsList style={{ overflowY: "unset" }}>
            {lessons.map((lesson, index) => (
              <LessonItem
                key={lesson.id}
                role="button"
                tabIndex={0}
                aria-disabled={!lesson.isUnlocked}
                aria-current={lesson.id === currentLessonId ? "true" : undefined}
                $active={lesson.id === currentLessonId}
                $unlocked={lesson.isUnlocked}
                $completed={lesson.progress?.isCompleted}
                onClick={() => handleLessonSelect(lesson)}
                onKeyDown={(e) => handleLessonKeyDown(e, lesson)}
              >
                <LessonIcon
                  $unlocked={lesson.isUnlocked}
                  $completed={lesson.progress?.isCompleted}
                >
                  {!lesson.isUnlocked ? (
                    <FaLock />
                  ) : lesson.progress?.isCompleted ? (
                    <FaCheckCircle />
                  ) : (
                    <FaPlay />
                  )}
                </LessonIcon>

                <LessonInfo>
                  <SidebarLessonTitle $small>
                    {index + 1}. {lesson.title}
                  </SidebarLessonTitle>
                  <LessonDuration>
                    {formatDuration(lesson.duration || 0)}
                  </LessonDuration>
                  {lesson.progress && (
                    <LessonProgress>
                      <ProgressBar>
                        <ProgressFill
                          width={lesson.progress.progressPercentage || 0}
                        />
                      </ProgressBar>
                      <span>
                        {Math.round(lesson.progress.progressPercentage || 0)}%
                      </span>
                    </LessonProgress>
                  )}
                </LessonInfo>
              </LessonItem>
            ))}
          </LessonsList>

          {courseCompleted && canApplyForCertificate && (
            <CertificateSection>
              <CertificateIcon>
                <CertificateIconCircle>
                  {certificateStatus?.status === "approved" ? (
                    <FaDownload />
                  ) : certificateStatus?.status === "pending" ? (
                    <FaHourglassHalf />
                  ) : (
                    <FaCertificate />
                  )}
                </CertificateIconCircle>
              </CertificateIcon>

              {!certificateStatus || !certificateStatus.status ? (
                <>
                  <CertificateTitle>Čestitamo!</CertificateTitle>
                  <CertificateDescription>
                    Uspešno ste završili sve lekcije kursa. Sada možete
                    aplicirati za sertifikat.
                  </CertificateDescription>
                  <CertificateBtn
                    onClick={onCertificateAction}
                    disabled={applicationLoading}
                  >
                    {applicationLoading ? (
                      <>
                        <l-line-spinner
                          size="18"
                          stroke="2"
                          speed="1"
                          color="var(--surface)"
                        />
                        Apliciranje...
                      </>
                    ) : (
                      <>
                        <FaCertificate />
                        Apliciraj za sertifikat
                      </>
                    )}
                  </CertificateBtn>
                </>
              ) : certificateStatus.status === "pending" ? (
                <>
                  <CertificateTitle>
                    Zahtev za sertifikat je poslat
                  </CertificateTitle>
                  <CertificateDescription>
                    Vaš zahtev za sertifikat je poslat na obradu. Molimo
                    sačekajte dok admin ne obradi vaš rad.
                  </CertificateDescription>
                  <CertificateBtn
                    disabled
                    $status={certificateStatus.status === "pending"}
                  >
                    <FaHourglassHalf />
                    Na obradi...
                  </CertificateBtn>
                </>
              ) : certificateStatus.status === "approved" ? (
                <>
                  <CertificateTitle>Sertifikat odobren!</CertificateTitle>
                  <CertificateDescription>
                    Čestitamo! Vaš sertifikat je odobren. Možete ga sada
                    preuzeti.
                  </CertificateDescription>
                  <CertificateBtn onClick={onCertificateAction}>
                    <FaDownload />
                    Preuzmi sertifikat
                  </CertificateBtn>
                </>
              ) : null}
            </CertificateSection>
          )}
        </LessonsListContainer>
      </SidebarContainerMobile>
    </div>
  );
};

export default GlobalVideoSidebar;
